import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '@/store/auth';
import { ElMessage } from 'element-plus';
import { config } from '@/config/config';

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: 'https://aivitahelper.xyz:8081/api', // 硬编码API基础URL
  timeout: 15000, // 请求超时时间
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    if (authStore.token) {
      // 确保headers对象存在
      config.headers = config.headers || {};
      // 设置Authorization头
      config.headers['Authorization'] = `Bearer ${authStore.token}`;
    }
    return config;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查响应状态码
    if (response.status !== 200) {
      ElMessage.error('请求失败');
      return Promise.reject(new Error('请求失败'));
    }

    // 检查是否是错误响应
    if (response.data && response.data.error) {
      ElMessage.error(response.data.error || '请求失败');
      return Promise.reject(new Error(response.data.error || '请求失败'));
    }

    // 如果是标准的包装响应格式（带有code字段）
    if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'code')) {
      const res = response.data;
      if (res.code !== 200) {
        ElMessage.error(res.message || '请求失败');
        if (res.code === 401) {
          const authStore = useAuthStore();
          authStore.logout();
        }
        return Promise.reject(new Error(res.message || '请求失败'));
      }
      return res.data;
    }

    // 如果是分页响应格式
    if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'content')) {
      console.log('处理分页数据', response.data);
      return response.data;
    }

    // 其他情况，直接返回响应数据
    return response.data;
  },
  (error) => {
    console.error('响应错误:', error);
    // 处理401未授权错误或403访问被拒绝错误
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const authStore = useAuthStore();
      // 显示相应的错误消息
      let errorMsg = '登录已过期，请重新登录';
      if (error.response.status === 403) {
        errorMsg = '访问被拒绝，请重新登录';
      }
      ElMessage.error(errorMsg);
      // 登出用户
      authStore.logout();
    } else {
      ElMessage.error(error.response?.data?.message || error.message || '请求失败');
    }
    return Promise.reject(error);
  }
);

// 请求函数
export const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => 
    service.get<any, T>(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    service.post<any, T>(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    service.put<any, T>(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => 
    service.delete<any, T>(url, config)
}; 